import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Chessboard } from "react-chessboard";
import { api } from "../lib/api";
import type { Game, GameDetail, LegalMove, MoveRow, User } from "../lib/types";

interface Props {
  user: User;
  opponentsCache: Map<number, string>;
}

function fmtClock(ms: number | null): string {
  if (ms === null || ms === undefined) return "—";
  const sec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function gameOverHeadline(game: Game, you: User): string {
  if (game.status !== "finished") return "";
  if (game.winnerUserId === null) return `Draw (${game.endReason ?? ""})`;
  if (game.winnerUserId === you.id) return `You won (${game.endReason ?? ""})`;
  return `You lost (${game.endReason ?? ""})`;
}

export function GamePage({ user, opponentsCache }: Props) {
  const { id } = useParams<{ id: string }>();
  const gameId = Number(id);
  const navigate = useNavigate();

  const [detail, setDetail] = useState<GameDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [selectedSquare, setSelectedSquare] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const reloadRef = useRef(() => {});

  const reload = useCallback(async () => {
    try {
      const d = await api.getGame(gameId);
      setDetail(d);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [gameId]);
  reloadRef.current = reload;

  useEffect(() => {
    reload();
  }, [reload]);

  // SSE subscribe
  useEffect(() => {
    if (!Number.isFinite(gameId)) return;
    const src = new EventSource(`/api/games/${gameId}/stream`);
    const onAny = () => reloadRef.current();
    src.addEventListener("move", onAny);
    src.addEventListener("resign", onAny);
    src.addEventListener("timeout", onAny);
    src.addEventListener("draw_offer", onAny);
    src.addEventListener("draw_agreed", onAny);
    src.addEventListener("draw_declined", onAny);
    src.onerror = () => {
      // Browser auto-reconnects; nothing to do.
    };
    return () => src.close();
  }, [gameId]);

  // Tick for clock display.
  useEffect(() => {
    if (!detail || detail.game.mode !== "live" || detail.game.status !== "active") return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [detail]);

  const yourColor = detail?.yourColor ?? null;
  const game = detail?.game;
  const yourTurn = game && game.status === "active" && yourColor === game.turn;
  const oppId = game ? (game.whiteUserId === user.id ? game.blackUserId : game.whiteUserId) : 0;
  const oppName = opponentsCache.get(oppId) ?? `Player ${oppId}`;

  const liveDisplay = useMemo(() => {
    if (!game || game.mode !== "live") {
      return { white: game?.whiteClockMs ?? null, black: game?.blackClockMs ?? null };
    }
    let w = game.whiteClockMs;
    let b = game.blackClockMs;
    if (game.status === "active" && game.lastMoveAt !== null) {
      const elapsed = now - game.lastMoveAt;
      if (game.turn === "w" && w !== null) w = Math.max(0, w - elapsed);
      if (game.turn === "b" && b !== null) b = Math.max(0, b - elapsed);
    }
    return { white: w, black: b };
  }, [game, now]);

  const legalSet = useMemo(() => {
    const map = new Map<string, LegalMove[]>();
    for (const m of detail?.legalMoves ?? []) {
      const list = map.get(m.from) ?? [];
      list.push(m);
      map.set(m.from, list);
    }
    return map;
  }, [detail]);

  async function tryMove(from: string, to: string): Promise<boolean> {
    if (!game || !yourTurn) return false;
    const candidates = legalSet.get(from) ?? [];
    const match = candidates.find((c) => c.to === to);
    if (!match) return false;
    try {
      const body: { from: string; to: string; promotion?: string } = { from, to };
      if (match.promotion) body.promotion = "q";
      await api.submitMove(game.id, body);
      await reload();
      setSelectedSquare(null);
      return true;
    } catch (e) {
      setToast((e as Error).message);
      setTimeout(() => setToast(null), 2500);
      return false;
    }
  }

  function onSquareClick(square: string) {
    if (!yourTurn) return;
    if (selectedSquare && selectedSquare !== square) {
      void tryMove(selectedSquare, square);
      return;
    }
    if (legalSet.has(square)) {
      setSelectedSquare(square === selectedSquare ? null : square);
    } else {
      setSelectedSquare(null);
    }
  }

  async function onResign() {
    if (!game) return;
    if (!confirm("Resign this game?")) return;
    try {
      await api.resign(game.id);
      await reload();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function onOfferDraw() {
    if (!game) return;
    try { await api.offerDraw(game.id); await reload(); } catch (e) { setError((e as Error).message); }
  }
  async function onAcceptDraw() {
    if (!game) return;
    try { await api.acceptDraw(game.id); await reload(); } catch (e) { setError((e as Error).message); }
  }
  async function onDeclineDraw() {
    if (!game) return;
    try { await api.declineDraw(game.id); await reload(); } catch (e) { setError((e as Error).message); }
  }

  if (error) return <div className="page"><p className="big" style={{ color: "var(--warn)" }}>{error}</p></div>;
  if (!detail || !game) return <div className="page"><p className="muted">Loading…</p></div>;

  const banner =
    game.status === "finished" ? (
      <div className="status-banner finished">{gameOverHeadline(game, user)}</div>
    ) : yourTurn ? (
      <div className="status-banner your-turn">Your move</div>
    ) : (
      <div className="status-banner waiting">Waiting for {oppName}</div>
    );

  const orientation = yourColor === "b" ? "black" : "white";
  const highlightStyles: Record<string, React.CSSProperties> = {};
  if (selectedSquare) {
    highlightStyles[selectedSquare] = { background: "rgba(180, 83, 9, 0.35)" };
    for (const m of legalSet.get(selectedSquare) ?? []) {
      highlightStyles[m.to] = { background: "rgba(21, 128, 61, 0.35)" };
    }
  }
  const lastMove = detail.moves.at(-1);
  if (lastMove) {
    const from = lastMove.uci.slice(0, 2);
    const to = lastMove.uci.slice(2, 4);
    highlightStyles[from] = { ...highlightStyles[from], boxShadow: "inset 0 0 0 4px rgba(245, 158, 11, 0.5)" };
    highlightStyles[to] = { ...highlightStyles[to], boxShadow: "inset 0 0 0 4px rgba(245, 158, 11, 0.5)" };
  }

  const drawOffered = game.drawOfferedBy !== null;
  const drawByMe = drawOffered && game.drawOfferedBy === user.id;
  const drawByOpp = drawOffered && game.drawOfferedBy !== user.id;

  return (
    <div className="page page-pad-bottom">
      <div className="flex-row" style={{ justifyContent: "space-between", marginBottom: "0.5rem" }}>
        <button className="secondary" onClick={() => navigate("/")} style={{ minHeight: 44, padding: "0.5rem 0.75rem", fontSize: "1rem" }}>
          ← Home
        </button>
        <div className="muted">{game.mode === "live" ? "Live" : "Async"} · vs {oppName}</div>
      </div>

      {banner}

      {game.mode === "live" && (
        <div className="clocks">
          <div className={`clock-box ${game.turn === (yourColor === "w" ? "b" : "w") ? "active" : ""} ${(liveDisplay.black ?? Infinity) < 30000 ? "low" : ""}`}>
            <div className="name">{yourColor === "w" ? oppName : "You"} (Black)</div>
            <div className="time">{fmtClock(liveDisplay.black)}</div>
          </div>
          <div className={`clock-box ${game.turn === (yourColor === "w" ? "w" : "b") ? "active" : ""} ${(liveDisplay.white ?? Infinity) < 30000 ? "low" : ""}`}>
            <div className="name">{yourColor === "w" ? "You" : oppName} (White)</div>
            <div className="time">{fmtClock(liveDisplay.white)}</div>
          </div>
        </div>
      )}

      <div className="board-wrap">
        <Chessboard
          options={{
            position: game.fen,
            boardOrientation: orientation,
            allowDragging: yourTurn,
            onPieceDrop: ({ sourceSquare, targetSquare }: { sourceSquare: string; targetSquare: string | null }) => {
              if (!targetSquare) return false;
              void tryMove(sourceSquare, targetSquare);
              return true;
            },
            onSquareClick: ({ square }: { square: string }) => onSquareClick(square),
            squareStyles: highlightStyles,
            darkSquareStyle: { backgroundColor: "#a16207" },
            lightSquareStyle: { backgroundColor: "#f5deb3" },
          } as never}
        />
      </div>

      {game.status === "active" && (
        <div className="game-actions">
          {drawByOpp ? (
            <>
              <button onClick={onAcceptDraw}>Accept draw</button>
              <button className="secondary" onClick={onDeclineDraw}>Decline draw</button>
            </>
          ) : drawByMe ? (
            <button className="secondary" disabled>Draw offered…</button>
          ) : (
            <button className="secondary" onClick={onOfferDraw}>Offer draw</button>
          )}
          <button className="danger" onClick={onResign}>Resign</button>
        </div>
      )}

      <MoveList moves={detail.moves} />

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function MoveList({ moves }: { moves: MoveRow[] }) {
  if (moves.length === 0) return null;
  const rows: Array<{ num: number; white?: string; black?: string }> = [];
  for (let i = 0; i < moves.length; i += 2) {
    rows.push({
      num: Math.floor(i / 2) + 1,
      white: moves[i]?.san,
      black: moves[i + 1]?.san,
    });
  }
  return (
    <details style={{ marginTop: "1rem" }}>
      <summary className="big">Moves ({moves.length})</summary>
      <ul className="move-list">
        {rows.map((r) => (
          <>
            <li key={`n-${r.num}`} className="num">{r.num}.</li>
            <li key={`w-${r.num}`}>{r.white ?? ""}</li>
            <li key={`b-${r.num}`}>{r.black ?? ""}</li>
          </>
        ))}
      </ul>
    </details>
  );
}
